import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Save, Plus, X, ShieldAlert, Sparkles, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import PageShell from "../components/PageShell";

export default function Settings() {
  const [form, setForm] = useState(null);
  const [newPhone, setNewPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/settings", form);
      setForm(res.data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="p-8 text-slate-400">Loading…</div>;

  return (
    <PageShell
      ticker={[
        { label: "Console", value: "SETTINGS", live: true },
        { label: "Currency", value: form.currency + " " + form.currency_symbol },
        { label: "Phones", value: String((form.phones || []).length).padStart(2, "0"), mono: true },
      ]}
      eyebrow="Chapter · Business profile"
      title={<>Set the <em className="italic text-blue-700">house rules</em></>}
      description="Details shown on every invoice, receipt and export."
      colophon="Settings"
      maxWidth="820px"
    >
      <div data-testid="settings-page">

      <Card className="bg-white border border-slate-200 p-6 space-y-4">
        <div>
          <Label>Business Name</Label>
          <Input
            value={form.business_name}
            onChange={(e) => setForm({ ...form, business_name: e.target.value })}
            data-testid="settings-name"
          />
        </div>
        <div>
          <Label>Tagline</Label>
          <Input
            value={form.tagline}
            onChange={(e) => setForm({ ...form, tagline: e.target.value })}
            data-testid="settings-tagline"
          />
        </div>
        <div>
          <Label>Address</Label>
          <Input
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            data-testid="settings-address"
          />
        </div>
        <div>
          <Label>Phone Numbers</Label>
          <div className="space-y-2 mt-1">
            {(form.phones || []).map((p, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  value={p}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      phones: form.phones.map((x, j) => (j === i ? e.target.value : x)),
                    })
                  }
                  data-testid={`settings-phone-${i}`}
                />
                <button
                  onClick={() =>
                    setForm({ ...form, phones: form.phones.filter((_, j) => j !== i) })
                  }
                  className="text-slate-400 hover:text-red-600"
                  data-testid={`settings-phone-remove-${i}`}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="Add phone number"
                data-testid="settings-new-phone"
              />
              <Button
                variant="outline"
                onClick={() => {
                  if (!newPhone) return;
                  setForm({ ...form, phones: [...(form.phones || []), newPhone] });
                  setNewPhone("");
                }}
                data-testid="settings-phone-add"
              >
                <Plus size={14} />
              </Button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Currency</Label>
            <Input
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              data-testid="settings-currency"
            />
          </div>
          <div>
            <Label>Currency Symbol</Label>
            <Input
              value={form.currency_symbol}
              onChange={(e) => setForm({ ...form, currency_symbol: e.target.value })}
              data-testid="settings-symbol"
            />
          </div>
        </div>
        <div>
          <Label>Logo URL (optional)</Label>
          <Input
            value={form.logo_url || ""}
            onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
            placeholder="https://..."
            data-testid="settings-logo"
          />
        </div>

        <div className="pt-2">
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="settings-save-btn">
            <Save size={16} className="mr-2" /> {saving ? "Saving…" : "Save Settings"}
          </Button>
        </div>
      </Card>

      <DangerZone />

      </div>
    </PageShell>
  );
}

function DangerZone() {
  const [action, setAction] = useState(null); // "seed" | "reset-sales" | "reset-parties" | "reset-products" | "reset-all"
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const ACTIONS = {
    seed: {
      title: "Seed 100 Demo Products",
      description:
        "Adds 100 realistic heavy-machinery parts (bearings, filters, seals, hydraulics, undercarriage, etc.) with brands, prices, stock, and barcodes. Use this to populate a fresh production database.",
      confirm: "Seed products",
      endpoint: "/admin/seed-demo-products",
      body: (pin) => ({ pin }),
      danger: false,
    },
    "import-inventory": {
      title: "Import Afridi&Co. Master Inventory",
      description:
        "Loads the 529 real parts from the shop's master CSV (sets & pieces auto-parsed). Safe to run once on a fresh production database. Adds — does NOT delete anything. If your database already has these products, you'll get duplicates.",
      confirm: "Import inventory",
      endpoint: "/admin/import-inventory",
      body: (pin) => ({ pin }),
      danger: false,
    },
    "reset-sales": {
      title: "Delete ALL Sales & Invoices",
      description:
        "Removes every sale record and its ledger entries. Invoice numbering resets to INV-0001.",
      confirm: "Yes, delete all sales",
      endpoint: "/admin/reset",
      body: (pin) => ({ pin, target: "sales" }),
      danger: true,
    },
    "reset-parties": {
      title: "Delete ALL Parties & Ledgers",
      description:
        "Removes every customer and supplier record along with their entire ledger history.",
      confirm: "Yes, delete all parties",
      endpoint: "/admin/reset",
      body: (pin) => ({ pin, target: "parties" }),
      danger: true,
    },
    "reset-products": {
      title: "Delete ALL Inventory",
      description:
        "Removes every product from the inventory. Product code numbering resets to P-00001.",
      confirm: "Yes, delete all products",
      endpoint: "/admin/reset",
      body: (pin) => ({ pin, target: "products" }),
      danger: true,
    },
    "reset-all": {
      title: "Wipe Everything (Full Reset)",
      description:
        "Deletes sales, invoices, parties, ledgers AND products. Only Settings are preserved. This cannot be undone.",
      confirm: "Yes, wipe everything",
      endpoint: "/admin/reset",
      body: (pin) => ({ pin, target: "all" }),
      danger: true,
    },
  };

  const current = action ? ACTIONS[action] : null;

  const runAction = async () => {
    if (!pin || pin.length < 4) return toast.error("Enter the 4-digit PIN");
    setBusy(true);
    try {
      const res = await api.post(current.endpoint, current.body(pin));
      const d = res.data;
      let msg = "Done";
      if (d.added !== undefined) {
        msg = `Imported ${d.added} products`;
        if (d.skipped_empty_rows) msg += ` (skipped ${d.skipped_empty_rows} empty rows)`;
        if (d.regenerated_codes) msg += `, ${d.regenerated_codes} codes auto-generated`;
      } else {
        const parts = [];
        if (d.sales_deleted !== undefined) parts.push(`${d.sales_deleted} sales`);
        if (d.parties_deleted !== undefined) parts.push(`${d.parties_deleted} parties`);
        if (d.products_deleted !== undefined) parts.push(`${d.products_deleted} products`);
        if (d.ledger_deleted !== undefined) parts.push(`${d.ledger_deleted} ledger entries`);
        msg = "Deleted " + (parts.join(", ") || "nothing");
      }
      toast.success(msg);
      setAction(null);
      setPin("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mt-10">
        <div className="flex items-center gap-3 mb-4">
          <ShieldAlert size={18} className="text-red-600" />
          <div className="text-[10px] uppercase tracking-[0.28em] text-red-600 font-semibold">
            Danger Zone · PIN-protected
          </div>
        </div>
        <Card
          className="border border-red-200 bg-red-50/30 p-5"
          data-testid="danger-zone"
        >
          <div className="text-sm text-slate-700 mb-4 leading-relaxed">
            One-tap tools to reset the database or seed demo data. All operations require
            the 4-digit admin PIN. There is no undo.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              onClick={() => setAction("seed")}
              data-testid="danger-seed"
              className="flex items-center gap-3 px-4 py-3 rounded border border-slate-200 bg-white hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
            >
              <Sparkles size={16} className="text-blue-700" />
              <div>
                <div className="text-sm font-medium text-slate-900">
                  Seed 100 demo products
                </div>
                <div className="text-[11px] text-slate-500">
                  Populate a fresh database
                </div>
              </div>
            </button>
            <button
              onClick={() => setAction("import-inventory")}
              data-testid="danger-import-inventory"
              className="flex items-center gap-3 px-4 py-3 rounded border border-emerald-200 bg-white hover:bg-emerald-50 hover:border-emerald-400 transition-colors text-left"
            >
              <Sparkles size={16} className="text-emerald-700" />
              <div>
                <div className="text-sm font-medium text-slate-900">
                  Import my 529 parts (CSV)
                </div>
                <div className="text-[11px] text-slate-500">
                  Load the Afridi&amp;Co. master inventory
                </div>
              </div>
            </button>
            {[
              ["reset-sales", "Reset all Sales", "Delete every invoice & counters"],
              ["reset-parties", "Reset all Parties", "Delete customers, suppliers, ledgers"],
              ["reset-products", "Reset all Inventory", "Delete every product"],
              ["reset-all", "Wipe EVERYTHING", "Sales + parties + inventory"],
            ].map(([k, t, d]) => (
              <button
                key={k}
                onClick={() => setAction(k)}
                data-testid={`danger-${k}`}
                className="flex items-center gap-3 px-4 py-3 rounded border border-slate-200 bg-white hover:bg-red-50 hover:border-red-300 transition-colors text-left"
              >
                <Trash2 size={16} className="text-red-700" />
                <div>
                  <div className="text-sm font-medium text-slate-900">{t}</div>
                  <div className="text-[11px] text-slate-500">{d}</div>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <Dialog open={!!action} onOpenChange={(v) => !v && setAction(null)}>
        <DialogContent data-testid="danger-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock size={16} className={current?.danger ? "text-red-600" : "text-blue-700"} />
              {current?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 leading-relaxed">
            {current?.description}
          </div>
          {current?.danger && (
            <div className="text-xs uppercase tracking-[0.24em] text-red-600 border border-red-200 bg-red-50 rounded px-3 py-2">
              ⚠ This action is permanent
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
              Admin PIN (4 digits)
            </Label>
            <Input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              placeholder="••••"
              className="mt-1 h-11 tracking-[0.4em] text-center font-mono-tabular text-lg"
              autoFocus
              data-testid="danger-pin-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAction(null);
                setPin("");
              }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              onClick={runAction}
              disabled={busy || pin.length < 4}
              data-testid="danger-confirm-btn"
              className={
                current?.danger
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-blue-700 hover:bg-blue-800 text-white"
              }
            >
              {busy ? "Working…" : current?.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
